import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Award, Globe } from 'lucide-react';
import SEO from '../components/SEO';
import HeroSlideshow from '../components/HeroSlideshow';
import api from '../utils/api';

const DEFAULT_STATS = [
  { icon: 'Star', value: '500+', label: 'Premium Products' },
  { icon: 'Award', value: '20+', label: 'Years Experience' },
  { icon: 'Globe', value: '50+', label: 'Countries Served' },
];

// Fallback images used when a category has no image uploaded
const CATEGORY_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
  'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=600&q=80',
  'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=600&q=80',
  'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600&q=80',
  'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=600&q=80',
  'https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=600&q=80',
];

const ICON_MAP = { Star, Award, Globe };

export default function Home() {
  const [hero, setHero] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [categories, setCategories] = useState([]);
  const [ctaBanner, setCtaBanner] = useState(null);
  const [solutionsPreview, setSolutionsPreview] = useState(null);

  useEffect(() => {
    // Fetch CMS content (hero, stats, cta, solutions-preview)
    const cmsPromise = api.get('/content?page=home').then(res => {
      const items = res.data.content || [];
      const find = s => items.find(c => c.section === s);

      const h = find('hero');
      if (h) setHero(h);

      const st = find('stats');
      if (st?.extraData?.items?.length) setStats(st.extraData.items);

      const cta = find('cta-banner');
      if (cta) setCtaBanner(cta);

      const sol = find('solutions-preview');
      if (sol) setSolutionsPreview(sol);
    }).catch(() => {});

    // Fetch categories from DB (the source of truth)
    const catsPromise = api.get('/categories').then(res => {
      const dbCats = res.data.categories || [];
      setCategories(dbCats);
    }).catch(() => {});

    Promise.all([cmsPromise, catsPromise]).finally(() => setLoaded(true));
  }, []);

  const heroImages = hero
    ? (hero.images?.length > 0 ? hero.images : hero.image ? [hero.image] : [])
    : [];

  return (
    <>
      <SEO />

      {/* Hero — fully dynamic, only render after CMS data is fetched */}
      {loaded && (
        <HeroSlideshow
          images={heroImages}
          title={hero?.title || 'The Sample House'}
          subtitle={hero?.subtitle || 'Premium Sample Solutions'}
          description={hero?.body || 'Crafting excellence in every sample. Your trusted partner for premium textile sample solutions.'}
          ctaButtons={hero?.ctaButtons || []}
        />
      )}
      {!loaded && <div className="h-screen bg-gray-100" />}

      {/* Stats */}
      <section className="py-16 border-y border-gray-200" style={{ background: '#F7F5EE' }}>
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-3 gap-8">
          {stats.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Star;
            return (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                className="text-center">
                <Icon className="mx-auto mb-3" size={24} style={{ color: 'var(--color-accent)' }} />
                <div className="font-serif text-3xl mb-1" style={{ color: 'var(--color-base)' }}>{s.value}</div>
                <div className="text-xs tracking-widest uppercase mt-1" style={{ color: '#666666' }}>{s.label}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-3">Our Range</p>
          <h2 className="section-title">Product Categories</h2>
          <div className="gold-line" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(loaded ? categories : []).map((cat, i) => {
            const imgSrc = cat.image || CATEGORY_FALLBACK_IMAGES[i % CATEGORY_FALLBACK_IMAGES.length];
            return (
              <motion.div key={cat.id || i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group relative overflow-hidden aspect-[3/4] cursor-pointer">
                <img src={imgSrc} alt={cat.label}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  style={{ objectPosition: cat.objectPosition || '50% 50%' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-serif text-xl mb-3" style={{ color: '#F5F0E8' }}>{cat.label}</h3>
                  <Link to={`/products?category=${cat.id}`}
                    className="flex items-center gap-2 text-xs tracking-widest uppercase group-hover:gap-4 transition-all duration-300"
                    style={{ color: 'var(--color-accent)' }}>
                    Explore <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-32 overflow-hidden">
        {loaded && (
          <img
            src={ctaBanner?.image || ctaBanner?.images?.[0] || 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1920&q=80'}
            alt="CTA"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: ctaBanner?.extraData?.objectPosition || '50% 50%' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.60)' }} />
        <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: 'var(--color-accent)' }}>Discover More</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: '#F5F0E8' }}>
              {ctaBanner?.title || 'Explore Our Sample Solutions'}
            </h2>
            <p className="mb-10" style={{ color: '#D4CCBB' }}>
              {ctaBanner?.body || 'Visually rich, meticulously curated samples for every need.'}
            </p>
            <Link to={ctaBanner?.ctaButtons?.[0]?.link || '/solutions'} className="btn-gold">
              {ctaBanner?.ctaButtons?.[0]?.label || 'Our Solutions'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Solutions Preview */}
      <section className="py-24 px-6 max-w-7xl mx-auto text-center">
        <p className="text-gold text-xs tracking-[0.3em] uppercase mb-3">What We Offer</p>
        <h2 className="section-title">{solutionsPreview?.title || 'Sample Solutions'}</h2>
        <div className="gold-line" />
        <p className="max-w-xl mx-auto mb-10" style={{ color: '#555555' }}>
          {solutionsPreview?.body || 'From concept to creation, we provide end-to-end sample solutions tailored to your brand\'s vision.'}
        </p>
        <Link to={solutionsPreview?.ctaButtons?.[0]?.link || '/solutions'} className="btn-outline">
          {solutionsPreview?.ctaButtons?.[0]?.label || 'Learn More'}
        </Link>
      </section>
    </>
  );
}
